import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Components
import ImageButton from "new_components/molecules/ImageButton";
import DropdownContent from "new_components/atoms/DropdownContent";
import Text from "new_components/atoms/Text";

// Constants
import colors from "styles/colors";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import Icon from "new_components/atoms/Icon";
import typography, { applyTypography } from "styles/typography";
import { fontSize } from "styled/colors";

// Styled Components
const SelectBoxWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: ${({ width }) => width || "auto"};
  margin-left: 20px;
`;

const DropdownWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 8px;
  z-index: 1000;
`;

const StyledLabel = styled.label`
  position: absolute;
  top: ${({ isActive }) => (isActive ? "-8px" : "16px")};
  ${applyTypography(typography.body.regular)};
  font-size: ${({ isActive }) => (isActive ? "12px" : "14px")};
  color: ${({ isActive }) => (isActive ? colors.text.dark : colors.text.gray)};
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  background-color: transparent;
  padding: 0 4px;
  z-index: ${({ isActive }) => (isActive ? "1" : "0")};
  font-weight: 400;
  font-family: "Raleway", sans-serif;
  // If placeholder is present, label should start at 0 opacity and transition to 1 opacity when active
  opacity: ${({ placeholder, isActive }) => (placeholder && !isActive ? "0" : "1")};
  transition: opacity 0.01s ease-in-out;
`;

const SelectButtonWrapper = styled.div`
  padding: 16px 0 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  cursor: ${({ readOnly }) => readOnly ? 'default' : 'pointer'};

  .selected-icon {
    margin-right: 8px;
    display: flex;
    align-items: center;
  }
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  background-color: ${({ isSelected }) => 
    isSelected ? colors.neutral[200] : colors.background.white};
  transition: background-color 0.2s ease-in-out;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: ${colors.neutral[200]};
  }

  .icon-wrapper {
    margin-right: 8px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
`;

/* Helper functions */
// map a preSelected item (or a value string) to an option from the provided options list
const mapToOption = (item, options) => {
  if (typeof item === "string") {
    return options.find((o) => o.value === item) || null;
  }
  if (item && typeof item === "object" && item.id) {
    return (
      options.find((o) => {
        if (typeof o.value === "object" && o.value.id) {
          return o.value.id === item.id;
        }
        return false;
      }) || null
    );
  }
  return null;
};

// Compare two option objects (assumes uniqueness by the value or id)
const compareOption = (opt1, opt2) => {
  if (!opt1 || !opt2) return false;
  if (typeof opt1.value === "object" && typeof opt2.value === "object") {
    return opt1.value.id === opt2.value.id;
  }
  return opt1.value === opt2.value;
};

const SelectBox = ({
  label,
  options,
  onSelect,
  width,
  value,
  settings,
  selected,
  preSelected,
  placeholder,
  readOnly = false,
  maxChars = null,
}) => {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hovered, setHovered] = useState(null);

  // Determine multi-select mode
  const isMulti = settings?.includes("multi");

  // Initialize state by merging preSelected with any selected/value provided.
  const [selectedValue, setSelectedValue] = useState(() => {
    let initial = [];
    if (preSelected && preSelected.length > 0) {
      const preSelectedOptions = preSelected
        .map((item) => mapToOption(item, options))
        .filter(Boolean);
      initial = [...preSelectedOptions];
    }
    const initialSelected = selected !== undefined ? selected : value;
    if (isMulti) {
      if (Array.isArray(initialSelected)) {
        const selectedOptions = initialSelected
          .map((item) =>
            typeof item === "string"
              ? options.find((o) => o.value === item)
              : item
          )
          .filter(Boolean);
        // Merge unique options into initial.
        selectedOptions.forEach((opt) => {
          if (!initial.some((existing) => compareOption(existing, opt))) {
            initial.push(opt);
          }
        });
      } else if (initialSelected) {
        const opt =
          typeof initialSelected === "string"
            ? options.find((o) => o.value === initialSelected)
            : initialSelected;
        if (opt && !initial.some((existing) => compareOption(existing, opt))) {
          initial.push(opt);
        }
      }
      return initial;
    } else {
      // In single-select mode, prefer preSelected if available.
      if (initial.length > 0) return initial[0];
      if (Array.isArray(initialSelected)) {
        return initialSelected.length > 0
          ? typeof initialSelected[0] === "string"
            ? options.find((o) => o.value === initialSelected[0]) || null
            : initialSelected[0]
          : null;
      } else {
        return initialSelected
          ? typeof initialSelected === "string"
            ? options.find((o) => o.value === initialSelected) || null
            : initialSelected
          : null;
      }
    }
  });

  // When preSelected changes, merge in any new items rather than replacing the state.
  useEffect(() => {
    if (preSelected && Array.isArray(preSelected)) {
      const preSelectedOptions = preSelected
        .map((item) => mapToOption(item, options))
        .filter(Boolean);
      setSelectedValue((prev) => {
        if (isMulti) {
          const merged = prev?.length > 0 ? [...prev] : [];
          preSelectedOptions.forEach((opt) => {
            if (!merged.some((existing) => compareOption(existing, opt))) {
              merged.push(opt);
            }
          });
          return merged;
        } else {
          return preSelectedOptions[0] || prev;
        }
      });
    }
  }, [preSelected, options, isMulti]);

  // When selected/value props change, merge them into state.
  useEffect(() => {
    const newValue = selected !== undefined ? selected : value;
    
    if (isMulti) {
      let newValues = [];
      if (Array.isArray(newValue)) {
        newValues = newValue
          .map((item) =>
            typeof item === "string"
              ? options.find((o) => o.value === item)
              : item
          )
          .filter(Boolean);
      } else if (newValue) {
        const opt =
          typeof newValue === "string"
            ? options.find((o) => o.value === newValue)
            : newValue;
        if (opt) newValues = [opt];
      }
      setSelectedValue(newValues);
    } else {
      if (newValue === null) {
        setSelectedValue(null);
      } else if (Array.isArray(newValue)) {
        const opt =
          newValue.length > 0
            ? typeof newValue[0] === "string"
              ? options.find((o) => o.value === newValue[0]) || null
              : newValue[0]
            : null;
        setSelectedValue(opt);
      } else if (typeof newValue === "string") {
        setSelectedValue(options.find((o) => o.value === newValue) || null);
      } else {
        setSelectedValue(newValue || null);
      }
    }
  }, [selected, value, options, isMulti]);

  // Compatibility check when switching between multi and single modes.
  useEffect(() => {
    if (isMulti && !Array.isArray(selectedValue)) {
      setSelectedValue(selectedValue ? [selectedValue] : []);
    } else if (!isMulti && Array.isArray(selectedValue)) {
      setSelectedValue(selectedValue.length > 0 ? selectedValue[0] : null);
    }
  }, [isMulti]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleOptionClick = (option) => {
    if (isMulti) {
      setSelectedValue((prev) => {
        // Initialize prev as empty array if it's null
        const currentValue = prev || [];
        
        // Check if the option already exists in the union.
        const exists = currentValue.some((selected) =>
          compareOption(selected, option)
        );
        let updated;
        if (exists) {
          updated = currentValue.filter((selected) => !compareOption(selected, option));
        } else {
          updated = [...currentValue, option];
        }
        onSelect(updated.map((item) => item.value));
        return updated;
      });
    } else {
      if (
        selectedValue &&
        (typeof selectedValue.value === "object"
          ? selectedValue.value.id === option.value.id
          : selectedValue.value === option.value)
      ) {
        setSelectedValue(null);
        onSelect(null);
      } else {
        setSelectedValue(option);
        setIsOpen(false);
        onSelect(option.value);
      }
    }
  };

  const handleClickAway = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickAway);
    return () => document.removeEventListener("click", handleClickAway);
  }, []);

  // Display label from the current selection(s)
  const displayLabel = isMulti
    ? selectedValue?.length > 0 
      ? `Selected (${selectedValue.length})`
      : placeholder || ""
    : selectedValue?.label || placeholder || "";

  const isActive = isMulti ? selectedValue?.length > 0 : !!selectedValue;

  return (
    <SelectBoxWrapper width={width} ref={ref}>
      {/* Floating Label */}
      <StyledLabel placeholder={placeholder} isActive={isActive}>{label}</StyledLabel>

      {/* Select Button */}
      <SelectButtonWrapper onClick={readOnly ? undefined : handleToggle} readOnly={readOnly}>
        <ImageButton
          text={displayLabel}
          icon={readOnly ? undefined : ICON_SET.dropdown}
          iconPosition="right"
          textStyle={true}
          isDropdown={!readOnly}
          color={colors.text.gray}
          readOnly={readOnly}
          iconProps={{ rotate: isOpen ? 180 : 0 }}
        />
      </SelectButtonWrapper>

      {/* Dropdown Content */}
      {!readOnly && isOpen && (
        <DropdownWrapper onMouseLeave={() => setHovered(null)}>
          <DropdownContent scrollable maxHeight="200px">
            {isMulti && (
              <DropdownItem
                onClick={() => {
                  const allSelected = selectedValue?.length === options.length;
                  if (allSelected) {
                    setSelectedValue([]);
                    onSelect([]);
                  } else {
                    setSelectedValue([...options]);
                    onSelect(options.map(opt => opt.value));
                  }
                }}
                onMouseOver={() => setHovered({ label: "Select All", value: "select_all" })}
                isSelected={selectedValue?.length === options.length}
              >
                <Icon 
                  name={selectedValue?.length === options.length 
                    ? ICON_SET.checkBoxPostselected 
                    : ICON_SET.checkBoxUnselected} 
                  style={{ marginRight: 4 }} 
                />
                <Text typographyStyle={typography.body.regular}>
                  Select All
                </Text>
              </DropdownItem>
            )}
            {options.map((option, index) => {
              const isSelected = isMulti
                ? selectedValue?.some((sel) =>
                    sel?.value?.id
                      ? sel.value.id === option.value?.id
                      : sel.value === option.value
                  )
                : selectedValue?.value?.id
                ? selectedValue.value.id === option.value?.id
                : selectedValue?.value === option.value;

              const isHovered = hovered?.value === option.value;

              const iconName = isSelected
                ? ICON_SET.checkBoxPostselected
                : isHovered
                ? ICON_SET.checkBoxPreselected
                  : ICON_SET.checkBoxUnselected;

              const truncatedLabel = maxChars ? option.label.slice(0, maxChars) + "..." : option.label;

              return (
                <DropdownItem
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  onMouseOver={isMulti ? () => setHovered(option) : undefined}
                  isSelected={isSelected}
                >
                  {isMulti && (
                    <Icon name={iconName} style={{ marginRight: 4 }} />
                  )}
                  {option.icon && (
                    <div className="icon-wrapper">
                      <Icon name={option.icon} size="small" />
                    </div>
                  )}
                  {/* If maxChars is set, show the truncated label when hovered */}
                  <Text
                    typographyStyle={typography.body.regular}
                    title={option.label}
                    style={{
                      fontSize: maxChars ? '10px' : "16px",
                      maxWidth: maxChars ? '300px' : "none",
                      width: '100%'
                    }}
                  >
                    {truncatedLabel}
                  </Text>
                </DropdownItem>
              );
            })}
          </DropdownContent>
        </DropdownWrapper>
      )}
    </SelectBoxWrapper>
  );
};

SelectBox.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
      icon: PropTypes.string,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      icon: PropTypes.string,
    }),
    PropTypes.array,
  ]),
  settings: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  preSelected: PropTypes.arrayOf(PropTypes.object),
};

export default SelectBox;